import React from "react";
import {
  AccessTime,
  InfoOutlined,
  LocalShippingOutlined,
  LocationCityOutlined,
  LocationOnOutlined,
} from "@mui/icons-material";
import { Box, Grid, Tooltip } from "@mui/material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";

import { localizeDateTimeShort } from "utils/dateFormats";
import { useShipmentUnLoadingDetailContext } from "contexts/ShipmentUnLoadingDetailContext";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { useAuth } from "hooks";

const ShipmentUnLoadingBasicDetail = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();

  const { shipmentUnLoadingDetail, loadingShipmentUnLoading } =
    useShipmentUnLoadingDetailContext();

  // Check if cargoCondition is "Damaged"
  const isCargoDamaged = shipmentUnLoadingDetail?.cargoCondition?.name === "Damaged";

  return !loadingShipmentUnLoading ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Unloading ID"
            subtitle={shipmentUnLoadingDetail?.unLoadingId}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Loading"
            subtitle={
              (() => {
                const rawLoading = shipmentUnLoadingDetail?.loadingId;
                const loadings = Array.isArray(rawLoading)
                  ? rawLoading
                  : [rawLoading].filter(Boolean);

                const validLoadings = loadings.filter((l) => l?.loadingId);

                if (validLoadings.length === 0) return "N/A";

                const visibleLoadings = validLoadings.slice(0, 3);
                const hasMore = validLoadings.length > 3;
                const tooltipText = validLoadings.map((l) => l.loadingId).join(", ");

                if (isCustomer || isVendor || isContractor || isDriver) {
                  const displayText = visibleLoadings.map((l) => l.loadingId).join(", ") + (hasMore ? ", ..." : "");
                  return (
                    <Tooltip title={tooltipText} arrow>
                      <span>{displayText}</span>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip title={tooltipText} arrow>
                    <span>
                      {visibleLoadings.map((l, index) => (
                        <span key={l?._id || index}>
                          {index > 0 && ", "}
                          <OpenInNewTab
                            label={l.loadingId}
                            link={`/app/crm/shipment-loading/${l?._id}/detail`}
                          />
                        </span>
                      ))}
                      {hasMore && ", ..."}
                    </span>
                  </Tooltip>
                );
              })()
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocationOnOutlined color="primary" />}
            title="Unloading Location"
            subtitle={
              shipmentUnLoadingDetail?.unloadingLocation
                ? `lat: ${shipmentUnLoadingDetail.unloadingLocation.lat || "N/A"}, lon: ${shipmentUnLoadingDetail.unloadingLocation.lon || "N/A"}`
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <AccessTime color="primary" />}
            title="Unloading Time"
            subtitle={localizeDateTimeShort(
              shipmentUnLoadingDetail?.unloadingTime
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocationCityOutlined color="primary" />}
            title="Unloading Branch"
            subtitle={
              shipmentUnLoadingDetail?.unLoadedBranch?.name
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocationCityOutlined color="primary" />}
            title="Transit Branch"
            subtitle={
              shipmentUnLoadingDetail?.transitBranch?.name
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Created By"
            subtitle={shipmentUnLoadingDetail?.createdBy?.name || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <AccessTime color="primary" />}
            title="Created At"
            subtitle={localizeDateTimeShort(
              shipmentUnLoadingDetail?.createdAt
            )}
          />
        </Grid>
        {isCargoDamaged && (
          <Grid item xs={3}>
            <InformationTile
              icon={() => <InfoOutlined color="primary" />}
              title="Cargo Condition Description"
              subtitle={shipmentUnLoadingDetail?.cargoConditionDescription || "N/A"}
            />
          </Grid>
        )}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Unloading Status"
            subtitle={shipmentUnLoadingDetail?.unloadingStatus || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Is Active?"
          >
            <Status isActive={shipmentUnLoadingDetail?.isActive} />
          </InformationTile>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default ShipmentUnLoadingBasicDetail;